import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Skeleton,
  Chip,
  alpha,
  useTheme,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  LinearProgress,
  Snackbar,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  MoreVert,
  Assessment,
  Speed,
  Error,
  Schedule,
  Description,
  Image,
  TableChart,
  Email,
  Warning,
  Refresh,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { useAuth0 } from '@auth0/auth0-react';
import MeterGrid from './MeterGrid';
import MeterChartsGrid from './MeterChartsGrid';
import AggregateHealthChart from './AggregateHealthChart';
import DraggableDashboard from './DraggableDashboard';
import CostOfNonCompliance from './CostOfNonCompliance';
import { useCapabilities } from '../contexts/CapabilitiesContext';

interface KPICardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  color?: string;
  loading?: boolean;
  subtitle?: string;
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  change,
  icon,
  color = '#0069AA', // Trindent Blue
  loading = false,
  subtitle,
}) => {
  const theme = useTheme();
  const isPositive = change && change > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        sx={{
          height: '100%',
          background: theme.palette.background.paper,
          backdropFilter: 'blur(20px)',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[1],
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: theme.shadows[3],
            transform: 'translateY(-2px)',
            transition: 'all 0.3s ease',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.6)} 100%)`,
          },
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {title}
              </Typography>
              {loading ? (
                <Skeleton width="60%" height={32} sx={{ mt: 1 }} />
              ) : (
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    mt: 1,
                    mb: 1,
                    background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${alpha(
                      theme.palette.text.primary,
                      0.7
                    )} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {value}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  {subtitle}
                </Typography>
              )}
              {change !== undefined && !loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  {isPositive ? (
                    <TrendingUp sx={{ fontSize: 16, color: theme.palette.success.main }} />
                  ) : (
                    <TrendingDown sx={{ fontSize: 16, color: theme.palette.error.main }} />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      color: isPositive ? theme.palette.success.main : theme.palette.error.main,
                      fontWeight: 600,
                    }}
                  >
                    {Math.abs(change)}% from last week
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: alpha(color, 0.1),
                color: color,
              }}
            >
              {icon}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

interface ModernDashboardProps {
  userEmail?: string;
  // Timestamp (ms) set when a preferences save triggered a fleet refresh elsewhere
  // (e.g. the Settings screen). The dashboard remounts on tab change with no knowledge
  // of that background refresh, so this signal lets it show the same recalculating cue.
  externalRefreshSignal?: number;
}

interface DashboardStats {
  totalDocuments: number;
  documentsToday: number;
  documentsThisWeek: number;
  avgConfidence: number;
  metersMonitored: number;
  criticalMeters: number;
  warningMeters: number;
  normalMeters: number;
  /** Warning tracks currently acknowledged. They are counted inside normalMeters (decision 6), so the
      Healthy card says how many of its meters are healthy only because someone parked the warning. */
  acknowledgedMeters: number;
  processingQueue: number;
  weeklyTrend: Array<{ day: string; documents: number; confidence: number }>;
  documentTypes: Array<{ name: string; value: number; color: string }>;
}

const ModernDashboard: React.FC<ModernDashboardProps> = ({ userEmail, externalRefreshSignal }) => {
  const theme = useTheme();
  const { getIdTokenClaims } = useAuth0();
  const { can } = useCapabilities();  // RBAC: gate Re-analyze on configure_meters
  const [loading, setLoading] = useState(true);
  // Independent loading for the Meter Status Overview cards: they only need
  // /meters/dashboard (same source the other meter widgets use), so they shouldn't
  // wait on the slower fleet/batch-history/user-uploads work the rest of the page does.
  const [meterLoading, setMeterLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // Bumped instead of calling window.location.reload(): the data effect below watches it and refetches
  // the dashboard in place, keeping scroll position, the open tab and every other widget's state.
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [confirmRefreshOpen, setConfirmRefreshOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardStats>({
    totalDocuments: 0,
    documentsToday: 0,
    documentsThisWeek: 0,
    avgConfidence: 0,
    metersMonitored: 0,
    criticalMeters: 0,
    warningMeters: 0,
    normalMeters: 0,
    acknowledgedMeters: 0,
    processingQueue: 0,
    weeklyTrend: [],
    documentTypes: [],
  });
  const [batchMeterData, setBatchMeterData] = useState<any>(null);

  const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'https://fg0jf2txt9.execute-api.us-east-2.amazonaws.com/prod';

  useEffect(() => {
    // Fetch JSON with a single retry (absorbs Lambda cold starts) and an ok-check, so a
    // transient 5xx never blows up on res.json() and shows the generic dashboard error.
    const fetchJson = async (url: string, hdrs: any, retries = 1): Promise<any> => {
      let lastErr: any;
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          const res = await fetch(url, { headers: hdrs });
          if (!res.ok) throw new (Error as any)(`HTTP ${res.status} for ${url}`);
          return await res.json();
        } catch (e) {
          lastErr = e;
          if (attempt < retries) await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
        }
      }
      throw lastErr;
    };

    const fetchDashboardData = async (silent = false) => {
      try {
        // Only the initial load shows the loading state; background polls update
        // the numbers in place so the KPI cards don't blank out every 45s.
        if (!silent) setLoading(true);
        setError(null);

        // Get auth token
        const tokenClaims = await getIdTokenClaims();
        const idToken = tokenClaims?.__raw;

        const headers = {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        };

        // Fetch the meter registry dashboard FIRST and render the Meter Status Overview
        // cards immediately — the same source the other meter widgets use. Everything
        // below (fleet, batch-history, full user-uploads pagination) is slower and only
        // feeds other sections, so it must not gate this summary.
        // Primary call — this is the ONLY one that gates the dashboard. Retried once to
        // absorb a cold start; if it still fails, that's a real "can't load" case.
        const meterRegistryData = await fetchJson(`${API_ENDPOINT}/meters/dashboard`, headers);

        // Parse the registry data (it may be wrapped in a body field)
        let registryResult;
        if (meterRegistryData.body) {
          registryResult = typeof meterRegistryData.body === 'string'
            ? JSON.parse(meterRegistryData.body)
            : meterRegistryData.body;
        } else {
          registryResult = meterRegistryData;
        }

        // Push the meter counts to state and clear the meter-summary loading NOW.
        if (registryResult?.summary) {
          const s = registryResult.summary;
          setDashboardData(prev => ({
            ...prev,
            metersMonitored: s.total_meters || 0,
            criticalMeters: s.critical_count || 0,
            warningMeters: s.warning_count || 0,
            normalMeters: s.healthy_count || 0,
            acknowledgedMeters: s.acknowledged_count || 0,
          }));
        }
        setMeterLoading(false);

        // Fetch fleet analytics (feeds other cards) — NON-CRITICAL: a transient failure here
        // must not blank the whole dashboard, so it degrades to null.
        let fleetData: any = null;
        try {
          fleetData = await fetchJson(`${API_ENDPOINT}/cal/analytics/fleet`, headers);
        } catch (e) {
          console.warn('Dashboard: fleet analytics failed (non-critical):', e);
        }

        // Fetch batch meter data for aggregate charts — NON-CRITICAL.
        try {
          const batchData = await fetchJson(`${API_ENDPOINT}/meters/batch-history`, headers);
          const batchResult = batchData?.body
            ? (typeof batchData.body === 'string' ? JSON.parse(batchData.body) : batchData.body)
            : batchData;
          setBatchMeterData(batchResult);
        } catch (e) {
          console.warn('Dashboard: batch-history failed (non-critical):', e);
        }

        // Fetch ALL user uploads with pagination — NON-CRITICAL: if this fails we keep the
        // document stats already on screen rather than blanking the dashboard.
        let allUploads: any[] = [];
        let uploadsOk = true;
        try {
          let lastKey: string | null = null;
          let hasMore = true;
          while (hasMore) {
            const url: string = lastKey
              ? `${API_ENDPOINT}/user-uploads?limit=100&lastKey=${encodeURIComponent(lastKey)}`
              : `${API_ENDPOINT}/user-uploads?limit=100`;
            const data = await fetchJson(url, headers);
            if (data.uploads) {
              allUploads = [...allUploads, ...data.uploads];
            }
            lastKey = data.lastKey;
            hasMore = data.hasMore || false;
            // Safety check to prevent infinite loops
            if (allUploads.length > 10000) {
              console.warn('Too many uploads, stopping pagination');
              break;
            }
          }
        } catch (e) {
          console.warn('Dashboard: user-uploads failed (non-critical):', e);
          uploadsOk = false;
        }

        const userUploads = { uploads: allUploads };

        // Process user uploads for statistics
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

        let totalDocs = 0;
        let todayDocs = 0;
        let weekDocs = 0;
        let totalConfidence = 0;
        let confidenceCount = 0;
        const typeCount: { [key: string]: number } = {};
        const dailyCount: { [key: string]: { count: number; confidence: number; confCount: number } } = {};

        // Initialize daily counts for the past week
        for (let i = 6; i >= 0; i--) {
          const date = new Date(today.getTime() - i * 24 * 60 * 60 * 1000);
          const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
          dailyCount[dayName] = { count: 0, confidence: 0, confCount: 0 };
        }

        if (userUploads.uploads) {
          userUploads.uploads.forEach((upload: any) => {
            totalDocs++;

            // Parse upload date
            let uploadDate: Date;
            try {
              // Handle different date formats
              if (upload.upload_timestamp) {
                uploadDate = new Date(upload.upload_timestamp);
              } else if (upload.ingestion_timestamp) {
                uploadDate = new Date(parseInt(upload.ingestion_timestamp));
              } else {
                return;
              }

              if (isNaN(uploadDate.getTime())) return;

              // Count by time period
              if (uploadDate >= today) {
                todayDocs++;
              }
              if (uploadDate >= weekAgo) {
                weekDocs++;

                // Add to daily count
                const dayName = uploadDate.toLocaleDateString('en-US', { weekday: 'short' });
                if (dailyCount[dayName]) {
                  dailyCount[dayName].count++;

                  // Add confidence if available
                  if (upload.overall_confidence && upload.overall_confidence > 0) {
                    dailyCount[dayName].confidence += upload.overall_confidence * 100;
                    dailyCount[dayName].confCount++;
                  }
                }
              }

              // Calculate average confidence
              if (upload.overall_confidence && upload.overall_confidence > 0) {
                totalConfidence += upload.overall_confidence * 100;
                confidenceCount++;
              }

              // Count by file type
              if (upload.original_filename) {
                const extension = upload.original_filename.split('.').pop()?.toLowerCase();
                switch (extension) {
                  case 'pdf':
                    typeCount['PDF'] = (typeCount['PDF'] || 0) + 1;
                    break;
                  case 'png':
                  case 'jpg':
                  case 'jpeg':
                    typeCount['Images'] = (typeCount['Images'] || 0) + 1;
                    break;
                  case 'xlsx':
                  case 'xls':
                    typeCount['Excel'] = (typeCount['Excel'] || 0) + 1;
                    break;
                  case 'eml':
                    typeCount['Email'] = (typeCount['Email'] || 0) + 1;
                    break;
                  case 'txt':
                  case 'csv':
                    typeCount['Text'] = (typeCount['Text'] || 0) + 1;
                    break;
                  default:
                    typeCount['Other'] = (typeCount['Other'] || 0) + 1;
                }
              }
            } catch (err) {
              console.error('Error parsing date:', err);
            }
          });
        }

        // Calculate weekly trend
        const weeklyTrend = Object.entries(dailyCount).map(([day, data]) => ({
          day,
          documents: data.count,
          confidence: data.confCount > 0 ? Math.round(data.confidence / data.confCount) : 0,
        }));

        // Calculate document type distribution
        const totalTypes = Object.values(typeCount).reduce((sum, count) => sum + count, 0);
        const documentTypes = Object.entries(typeCount)
          .map(([name, count]) => ({
            name,
            value: totalTypes > 0 ? Math.round((count / totalTypes) * 100) : 0,
            color: getTypeColor(name),
          }))
          .filter(type => type.value > 0)
          .sort((a, b) => b.value - a.value);

        // Process fleet data - Use MeterProductRegistry as the source of truth
        let metersMonitored = 0;
        let criticalMeters = 0;
        let warningMeters = 0;
        let normalMeters = 0;
        let acknowledgedMeters = 0;

        if (registryResult && registryResult.summary) {
          // Use the MeterProductRegistry summary data
          metersMonitored = registryResult.summary.total_meters || 0;
          criticalMeters = registryResult.summary.critical_count || 0;
          warningMeters = registryResult.summary.warning_count || 0;
          normalMeters = registryResult.summary.healthy_count || 0;
          acknowledgedMeters = registryResult.summary.acknowledged_count || 0;
        } else if (registryResult && registryResult.meter_products) {
          // If summary is not available, calculate from meter_products array
          metersMonitored = new Set(
            registryResult.meter_products.map((mp: any) => mp.meter_id)
          ).size;

          registryResult.meter_products.forEach((mp: any) => {
            if (mp.acknowledgement) acknowledgedMeters++;
            switch (mp.display_health || mp.current_health) {
              case 'red':
                criticalMeters++;
                break;
              case 'yellow':
                warningMeters++;
                break;
              case 'green':
                normalMeters++;
                break;
            }
          });
        } else if (fleetData) {
          // Fall back to fleetData if MeterProductRegistry is empty
          metersMonitored = fleetData.total_meters || 0;

          // Use the count fields if available (from fast summary)
          if (fleetData.total_meters_normal !== undefined) {
            normalMeters = fleetData.total_meters_normal;
            warningMeters = fleetData.total_meters_warning || 0;
            criticalMeters = fleetData.total_meters_critical || 0;
          } else {
            // Fall back to array lengths for detailed analysis
            criticalMeters = fleetData.meters_needing_attention?.length || 0;
            normalMeters = fleetData.meters_normal?.length || 0;
            // Calculate warning meters (total - critical - normal)
            warningMeters = Math.max(0, metersMonitored - criticalMeters - normalMeters);
          }
        }

        // Calculate percentage changes
        const lastWeekDocs = weekDocs > 0 ? weekDocs - todayDocs : 0;
        const weekChange = lastWeekDocs > 0 ? Math.round(((todayDocs - lastWeekDocs) / lastWeekDocs) * 100) : 0;

        setDashboardData(prev => ({
          ...prev,
          // Meter counts come from the primary registry call — always update them.
          metersMonitored,
          criticalMeters,
          warningMeters,
          normalMeters,
          acknowledgedMeters,
          processingQueue: 0, // TODO: Get from upload manager
          // Document stats come from /user-uploads — only overwrite when that succeeded, so a
          // transient failure keeps the numbers already on screen instead of zeroing them.
          ...(uploadsOk ? {
            totalDocuments: totalDocs,
            documentsToday: todayDocs,
            documentsThisWeek: weekDocs,
            avgConfidence: confidenceCount > 0 ? Math.round(totalConfidence / confidenceCount) : 0,
            weeklyTrend,
            documentTypes,
          } : {}),
        }));

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        if (!silent) setLoading(false);
        setMeterLoading(false);
      }
    };

    if (userEmail) {
      fetchDashboardData();
      // Poll so an open dashboard stays current with backend recomputes (adds,
      // edits, renames, baseline resets all land within ~45s) — silently, so the
      // numbers refresh in place instead of blanking.
      const interval = setInterval(() => fetchDashboardData(true), 45000);
      // ...and refetch immediately whenever the user returns to the tab.
      const onVisible = () => {
        if (document.visibilityState === 'visible') fetchDashboardData(true);
      };
      document.addEventListener('visibilitychange', onVisible);
      return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', onVisible);
      };
    }
  }, [userEmail, getIdTokenClaims, API_ENDPOINT, refreshNonce]);

  const handleManualRefresh = async () => {
    if (refreshing) return;

    setRefreshing(true);
    try {
      // Get auth token
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const headers = {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      };

      // Full recalculation of every meter-product's stats, health and control limits
      // from the current settings (same as triggering the aggregator's refresh_all).
      console.log('Triggering full meter statistics recalculation...');
      const response = await fetch(`${API_ENDPOINT}/meters/refresh`, {
        method: 'POST',
        headers,
      });

      if (!response.ok) {
        const errText = await response.text();
        console.error('Meter refresh failed:', response.status, errText);
        setError('Failed to recalculate meter statistics. Please try again.');
        return;
      }

      // The recalculation runs asynchronously (~1 minute for the full fleet).
      // Wait for it to finish, then refetch so the dashboard shows the rebuilt data.
      await new Promise(resolve => setTimeout(resolve, 60000));

      setLastRefreshed(new Date());
      setRefreshNonce((n) => n + 1);
    } catch (error) {
      console.error('Manual refresh failed:', error);
      setError('Failed to refresh analytics. Please try again.');
    } finally {
      setRefreshing(false);
    }
  };

  // A preferences save (Settings) triggered a background fleet refresh. The dashboard
  // just remounted with no in-flight state, so show the SAME recalculating cue (spinning
  // button + toast + progress) and reload once it's had time to land -- keeping the
  // experience identical to a manual refresh. Guarded on recency so an old signal from a
  // previous session/mount doesn't re-trigger.
  useEffect(() => {
    if (!externalRefreshSignal) return;
    const elapsed = Date.now() - externalRefreshSignal;
    if (elapsed < 0 || elapsed > 90000) return;  // only a fresh signal
    if (refreshing) return;
    setRefreshing(true);
    const remaining = Math.max(3000, 60000 - elapsed);
    const t = setTimeout(() => {
      setLastRefreshed(new Date());
      setRefreshNonce((n) => n + 1);
      setRefreshing(false);
    }, remaining);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalRefreshSignal]);

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'PDF': return '#FF5252';
      case 'Images': return '#00D67E';
      case 'Excel': return '#0066FF';
      case 'Email': return '#FFB020';
      case 'Text': return '#8B9AAB';
      default: return '#6B7C8F';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'PDF': return <Description />;
      case 'Images': return <Image />;
      case 'Excel': return <TableChart />;
      case 'Email': return <Email />;
      default: return <Description />;
    }
  };

  // Memoize dashboard sections to prevent flickering
  const dashboardSections = useMemo(() => [
    {
      id: 'meter-fleet-status',
      title: 'Meter Status Overview',
      component: (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Total Meters"
                value={dashboardData.metersMonitored}
                icon={<Assessment />}
                color={theme.palette.primary.main}
                loading={meterLoading}
                subtitle="Unique physical meters"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Critical"
                value={dashboardData.criticalMeters}
                icon={<Error />}
                color={theme.palette.error.main}
                loading={meterLoading}
                subtitle="Meter-product pairs"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Warning"
                value={dashboardData.warningMeters}
                icon={<Warning />}
                color={theme.palette.warning.main}
                loading={meterLoading}
                subtitle="Meter-product pairs"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Healthy"
                value={dashboardData.normalMeters}
                icon={<Speed />}
                color={theme.palette.success.main}
                loading={meterLoading}
                subtitle={dashboardData.acknowledgedMeters > 0
                  ? `Meter-product pairs — incl. ${dashboardData.acknowledgedMeters} acknowledged`
                  : 'Meter-product pairs'}
              />
            </Grid>
          </Grid>
        </Box>
      ),
    },
    {
      id: 'meter-health-grid',
      title: 'Meter Health Monitor',
      component: <MeterGrid />,
    },
    {
      id: 'meter-control-charts',
      title: 'Meter Factor Control Charts',
      component: <MeterChartsGrid />,
    },
    {
      id: 'cost-of-non-compliance',
      title: 'Cost of Non-Compliance',
      component: <CostOfNonCompliance isDashboard={true} />,
    },
    {
      id: 'fleet-health-overview',
      title: 'Master Meter Health Overview',
      component: <AggregateHealthChart data={batchMeterData} selectedProduct="all" />,
    },
    {
      id: 'document-metrics',
      title: 'Document Processing Metrics',
      component: (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Total Documents"
                value={dashboardData.totalDocuments.toLocaleString()}
                change={dashboardData.documentsThisWeek > 0 ?
                  Math.round((dashboardData.documentsThisWeek / Math.max(1, dashboardData.totalDocuments - dashboardData.documentsThisWeek)) * 100) : 0}
                icon={<Assessment />}
                color={theme.palette.primary.main}
                loading={loading}
                subtitle="All time"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Today's Uploads"
                value={dashboardData.documentsToday}
                change={dashboardData.documentsThisWeek > 0 && dashboardData.documentsToday > 0 ?
                  Math.round(((dashboardData.documentsToday - (dashboardData.documentsThisWeek / 7)) / Math.max(1, dashboardData.documentsThisWeek / 7)) * 100) : 0}
                icon={<Schedule />}
                color={theme.palette.info.main}
                loading={loading}
                subtitle="Last 24 hours"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KPICard
                title="Avg. Confidence"
                value={`${dashboardData.avgConfidence}%`}
                change={dashboardData.avgConfidence > 90 ? 5 : -3}
                icon={<Speed />}
                color={theme.palette.success.main}
                loading={loading}
                subtitle="AI accuracy"
              />
            </Grid>
          </Grid>
        </Box>
      ),
    },
    {
      id: 'analytics-charts',
      title: 'Analytics & Trends',
      component: (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={3}>
            {/* Weekly Trend Chart */}
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  background: theme.palette.background.paper,
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Weekly Processing Trend
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        Documents processed and confidence scores
                      </Typography>
                    </Box>
                    <IconButton size="small">
                      <MoreVert />
                    </IconButton>
                  </Box>
                  {loading ? (
                    <Skeleton variant="rectangular" height={300} />
                  ) : dashboardData.weeklyTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={dashboardData.weeklyTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                        <XAxis dataKey="day" stroke={theme.palette.text.secondary} />
                        <YAxis stroke={theme.palette.text.secondary} />
                        <RechartsTooltip
                          contentStyle={{
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '8px',
                          }}
                        />
                        <Area
                          type="monotone"
                          dataKey="documents"
                          stroke={theme.palette.primary.main}
                          fill={alpha(theme.palette.primary.main, 0.1)}
                          strokeWidth={2}
                          name="Documents"
                        />
                        <Area
                          type="monotone"
                          dataKey="confidence"
                          stroke={theme.palette.success.main}
                          fill={alpha(theme.palette.success.main, 0.1)}
                          strokeWidth={2}
                          name="Confidence %"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No data available for this week
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Document Types Pie Chart */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  background: theme.palette.background.paper,
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${theme.palette.divider}`,
                  height: '100%',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Document Types
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        Distribution by format
                      </Typography>
                    </Box>
                  </Box>
                  {loading ? (
                    <Skeleton variant="circular" width={180} height={180} sx={{ mx: 'auto', my: 4 }} />
                  ) : dashboardData.documentTypes.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={dashboardData.documentTypes}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={5}
                            dataKey="value"
                          >
                            {dashboardData.documentTypes.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <RechartsTooltip />
                        </PieChart>
                      </ResponsiveContainer>
                      <Box sx={{ mt: 2 }}>
                        {dashboardData.documentTypes.map((type) => (
                          <Box
                            key={type.name}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 1,
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '3px',
                                  backgroundColor: type.color,
                                }}
                              />
                              <Typography variant="body2">{type.name}</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {type.value}%
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ height: 250, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No documents uploaded yet
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      ),
    },
  ], [dashboardData, loading, theme, batchMeterData]);

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            Welcome back, {userEmail || 'User'}. Here's your real-time analytics overview.
          </Typography>
          {lastRefreshed && (
            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, mt: 0.5, display: 'block' }}>
              Last refreshed: {lastRefreshed.toLocaleTimeString()}
            </Typography>
          )}
        </Box>
        <Box>
          <IconButton
            onClick={() => setConfirmRefreshOpen(true)}
            disabled={refreshing || !can('configure_meters')}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.action.disabled,
                color: theme.palette.action.disabledBackground,
              }
            }}
            title={can('configure_meters')
              ? 'Re-analyze all meter data — recalculates health, control limits and regression for every meter from the current settings'
              : 'Requires the Meter Config (configure_meters) permission'}
          >
            <Refresh sx={{
              animation: refreshing ? 'spin 1s linear infinite' : 'none',
              '@keyframes spin': {
                from: { transform: 'rotate(0deg)' },
                to: { transform: 'rotate(360deg)' }
              }
            }} />
          </IconButton>
        </Box>
      </Box>

      {/* Prominent, unmissable cue that a full re-analysis is running (so the single
          top refresh can't be mistakenly spammed and the user knows it's working). */}
      <Snackbar
        open={refreshing}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 7 }}
      >
        <Alert severity="info" icon={false} sx={{ width: '100%', maxWidth: 460, boxShadow: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Refresh sx={{
              animation: 'spin 1s linear infinite',
              '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
            }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Re-analyzing all meter data… about a minute. The dashboard reloads automatically when it finishes.
            </Typography>
          </Box>
          <LinearProgress />
        </Alert>
      </Snackbar>

      {/* Confirm before kicking off a full fleet re-analysis. */}
      <Dialog open={confirmRefreshOpen} onClose={() => setConfirmRefreshOpen(false)}>
        <DialogTitle>Re-analyze all meter data?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This re-runs the full analysis for <strong>every</strong> meter — recalculating
            health, control limits and regression from the current settings. It takes about
            a minute, and the dashboard will reload when it's done.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRefreshOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => { setConfirmRefreshOpen(false); handleManualRefresh(); }}
            autoFocus
          >
            Re-analyze all data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Draggable Dashboard Sections (Document Processing Metrics + Analytics
          & Trends are intentionally excluded from the dashboard and digests) */}
      <DraggableDashboard
        storageKey="modernDashboardLayout"
        sections={dashboardSections.filter(
          (s) => !['document-metrics', 'analytics-charts'].includes(s.id),
        )}
      />
    </Box>
  );
};

export default ModernDashboard;
